/**
 * NahidStudio - WhatsApp Concierge & Order Handler
 * Ultra-lightweight, high-performance variant resolver & multilingual concierge
 */

(function () {
  'use strict';

  const I18N = {
    id: {
      btnProduct: 'Pesan via WhatsApp',
      btnCart: 'Selesaikan Pesanan via WhatsApp',
      consultText: 'Butuh panduan ukuran? Konsultasikan bersama Atelier via WhatsApp',
      consultMessage: (title, url) =>
        `Halo Nahid Studio, saya ingin konsultasi ukuran & panduan busana untuk koleksi ${title} (${url}).`,
      greetingProduct: 'Halo Nahid Studio, saya tertarik untuk memesan koleksi berikut:',
      greetingCart: 'Halo Nahid Studio, saya ingin menyelesaikan pesanan dari keranjang belanja:',
      labelProduct: 'Produk',
      labelSize: 'Ukuran',
      labelColor: 'Warna',
      labelVariant: 'Ukuran / Varian',
      labelCustom: 'Detail Kustom',
      labelQty: 'Jumlah',
      labelPrice: 'Estimasi Harga',
      labelTotal: 'Total Belanja',
      labelLink: 'Tautan',
      labelNotes: 'Catatan',
      orderSummaryHeading: 'Daftar Pesanan',
      customerHeading: 'Data Pemesan & Pengiriman',
      labelCustomerName: 'Nama Penerima',
      labelCustomerPhone: 'No. HP / WhatsApp',
      labelCustomerAddress: 'Alamat Lengkap',
      placeholderCustomerName: '[Isi Nama Lengkap]',
      placeholderCustomerPhone: '[Isi No. WhatsApp / HP]',
      placeholderCustomerAddress: '[Alamat, Kecamatan, Kota, Kode Pos]',
      closingProduct: 'Mohon dibantu konfirmasi ketersediaan stok, ongkir, dan panduan pembayarannya. Terima kasih.',
      closingCart: 'Mohon dibantu konfirmasi ongkir, proses pemesanan & instruksi pembayarannya. Terima kasih.',
      cartEmpty: 'Keranjang belanja Anda masih kosong.'
    },
    en: {
      btnProduct: 'Order via WhatsApp',
      btnCart: 'Complete Order via WhatsApp',
      consultText: 'Need sizing guidance? Consult our Atelier via WhatsApp',
      consultMessage: (title, url) =>
        `Dear Nahid Studio, I would like to consult on sizing & bespoke styling for ${title} (${url}).`,
      greetingProduct: 'Dear Nahid Studio, I would like to order the following collection:',
      greetingCart: 'Dear Nahid Studio, I would like to complete my order from the shopping bag:',
      labelProduct: 'Product',
      labelSize: 'Size',
      labelColor: 'Color',
      labelVariant: 'Size / Variant',
      labelCustom: 'Bespoke Measurements',
      labelQty: 'Quantity',
      labelPrice: 'Estimated Price',
      labelTotal: 'Total Amount',
      labelLink: 'Link',
      labelNotes: 'Notes',
      orderSummaryHeading: 'Order Summary',
      customerHeading: 'Customer & Delivery Details',
      labelCustomerName: 'Recipient Name',
      labelCustomerPhone: 'WhatsApp / Phone',
      labelCustomerAddress: 'Full Address',
      placeholderCustomerName: '[Your Full Name]',
      placeholderCustomerPhone: '[Your Phone Number]',
      placeholderCustomerAddress: '[Street Address, City, Postal Code]',
      closingProduct: 'Kindly assist with stock confirmation, shipping fee, and payment instructions. Thank you.',
      closingCart: 'Kindly assist with shipping calculation, order confirmation, and payment instructions. Thank you.',
      cartEmpty: 'Your shopping bag is currently empty.'
    }
  };

  function detectContext() {
    const docLang = (document.documentElement.lang || '').toLowerCase();
    const shopifyLocale = (window.Shopify?.locale || '').toLowerCase();
    const activeCurrency = (window.Shopify?.currency?.active || '').toUpperCase();
    const path = window.location.pathname.toLowerCase();

    let lang = 'id';
    if (
      docLang.startsWith('en') ||
      shopifyLocale.startsWith('en') ||
      path.includes('/en') ||
      (activeCurrency && activeCurrency !== 'IDR')
    ) {
      lang = 'en';
    } else if (docLang.startsWith('id') || shopifyLocale.startsWith('id')) {
      lang = 'id';
    }

    const currency = activeCurrency || 'IDR';
    return { lang, currency };
  }

  function formatMoney(cents, currencyCode) {
    if (!cents && cents !== 0) return '';
    const { lang, currency: detectedCurrency } = detectContext();
    const currency = (currencyCode || detectedCurrency || 'IDR').toUpperCase();
    const isZeroDecimal = ['JPY', 'KRW', 'VND'].includes(currency);
    const amount = isZeroDecimal ? Math.round(cents) : cents / 100;

    if (currency === 'IDR') {
      return 'Rp ' + Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    try {
      return new Intl.NumberFormat(lang === 'id' ? 'id-ID' : 'en-US', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  function cleanPhoneNumber(phone) {
    if (!phone) return '';
    let digits = phone.toString().replace(/[^0-9]/g, '');
    if (digits.startsWith('0')) {
      digits = '62' + digits.slice(1);
    }
    return digits;
  }

  function getLivePriceText(container) {
    if (!container) return '';
    const priceEl = container.querySelector(
      '.price .price-item--regular, .price-item--last, .price-item, .price, [data-price], text-component[ref="productPrice"]'
    );
    if (priceEl) {
      const text = (priceEl.textContent || '').trim();
      if (text && !text.includes('\n')) return text;
      if (text) {
        const lines = text
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean);
        return lines[lines.length - 1] || lines[0];
      }
    }
    return '';
  }

  function getCustomerDetails(btn, form) {
    let name = (btn?.getAttribute('data-customer-name') || '').trim();
    let phone = (btn?.getAttribute('data-customer-phone') || '').trim();
    let address = (btn?.getAttribute('data-customer-address') || '').trim();

    // Check if entered in current form
    if (form) {
      const nameInput = form.querySelector('input[name="customer[name]"], input[name="name"], input[name="properties[Customer Name]"], input[name="properties[Nama]"]');
      const phoneInput = form.querySelector('input[name="customer[phone]"], input[name="phone"], input[name="properties[Phone]"], input[name="properties[No HP]"]');
      const addressInput = form.querySelector('input[name="customer[address]"], textarea[name="address"], textarea[name="properties[Address]"], textarea[name="properties[Alamat]"]');
      if (nameInput?.value?.trim()) name = nameInput.value.trim();
      if (phoneInput?.value?.trim()) phone = phoneInput.value.trim();
      if (addressInput?.value?.trim()) address = addressInput.value.trim();
    }

    // Check localStorage cache if previously saved
    try {
      const cached = JSON.parse(localStorage.getItem('nhd_customer_info') || '{}');
      if (!name && cached.name) name = cached.name.trim();
      if (!phone && cached.phone) phone = cached.phone.trim();
      if (!address && cached.address) address = cached.address.trim();

      if (name || phone || address) {
        localStorage.setItem(
          'nhd_customer_info',
          JSON.stringify({
            name: name || cached.name || '',
            phone: phone || cached.phone || '',
            address: address || cached.address || ''
          })
        );
      }
    } catch (e) {}

    return { name, phone, address };
  }

  function formatCustomerSection(btn, form, dict) {
    const cust = getCustomerDetails(btn, form);
    const nameVal = cust.name || dict.placeholderCustomerName;
    const phoneVal = cust.phone || dict.placeholderCustomerPhone;
    const addressVal = cust.address || dict.placeholderCustomerAddress;

    return `\n*${dict.customerHeading}:*\n• *${dict.labelCustomerName}*: ${nameVal}\n• *${dict.labelCustomerPhone}*: ${phoneVal}\n• *${dict.labelCustomerAddress}*: ${addressVal}\n`;
  }

  // Debounced Sync of Dynamic Labels - Never mutates unless values differ
  let syncScheduled = false;
  function syncDynamicLabels() {
    if (syncScheduled) return;
    syncScheduled = true;
    requestAnimationFrame(() => {
      syncScheduled = false;
      const { lang } = detectContext();
      const dict = I18N[lang] || I18N.id;

      // Product WhatsApp Buttons
      const productButtons = document.querySelectorAll('[data-nhd-whatsapp-product]');
      for (let i = 0; i < productButtons.length; i++) {
        const btn = productButtons[i];
        const span = btn.querySelector('span');
        const textKey = lang === 'en' ? 'data-btn-text-en' : 'data-btn-text-id';
        const targetText = btn.getAttribute(textKey) || dict.btnProduct;
        if (span && span.textContent !== targetText) {
          span.textContent = targetText;
        }
        if (btn.getAttribute('aria-label') !== targetText) {
          btn.setAttribute('aria-label', targetText);
        }
      }

      // Sizing & Atelier Consultation Links
      const consultLinks = document.querySelectorAll('.nhd-whatsapp-consult-link');
      for (let i = 0; i < consultLinks.length; i++) {
        const link = consultLinks[i];
        const span = link.querySelector('span');
        const textKey = lang === 'en' ? 'data-consult-text-en' : 'data-consult-text-id';
        const targetText = link.getAttribute(textKey) || dict.consultText;
        if (span && span.textContent !== targetText) {
          span.textContent = targetText;
        }
        const rawPhone = link.getAttribute('data-phone');
        const phone = cleanPhoneNumber(rawPhone);
        const productTitle = link.getAttribute('data-product-title') || document.querySelector('.product-title, h1')?.textContent?.trim() || 'Nahid Studio';
        const productUrl = link.getAttribute('data-product-url') || window.location.href;
        if (phone) {
          const consultMsg = dict.consultMessage(productTitle, productUrl);
          const targetHref = `https://api.whatsapp.com/send?phone=${encodeURIComponent(phone)}&text=${encodeURIComponent(consultMsg)}`;
          if (link.getAttribute('href') !== targetHref) {
            link.setAttribute('href', targetHref);
          }
        }
      }

      // Cart WhatsApp Buttons
      const cartButtons = document.querySelectorAll('[data-nhd-whatsapp-cart]');
      for (let i = 0; i < cartButtons.length; i++) {
        const btn = cartButtons[i];
        const span = btn.querySelector('span');
        const textKey = lang === 'en' ? 'data-btn-text-en' : 'data-btn-text-id';
        const targetText = btn.getAttribute(textKey) || dict.btnCart;
        if (span && span.textContent !== targetText) {
          span.textContent = targetText;
        }
        if (btn.getAttribute('aria-label') !== targetText) {
          btn.setAttribute('aria-label', targetText);
        }
      }
    });
  }

  // Product Page Handler
  function initProductWhatsApp() {
    const productButtons = document.querySelectorAll('[data-nhd-whatsapp-product]');
    productButtons.forEach((btn) => {
      if (btn.dataset.initialized === 'true') return;
      btn.dataset.initialized = 'true';

      const section = btn.closest('.shopify-section') || btn.closest('[data-section-id]') || btn.closest('main') || document;

      function resolveCurrentVariant() {
        let variantId = null;
        let variantTitle = '';
        let variantPrice = '';

        const form = btn.closest('form') || section.querySelector('form[data-type="add-to-cart-form"]') || document.querySelector('form[data-type="add-to-cart-form"]');
        const idInput = form ? form.querySelector('input[name="id"]') : section.querySelector('input[name="id"]');
        if (idInput && idInput.value) {
          variantId = idInput.value.trim();
        }

        if (!variantId) {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.has('variant')) {
            variantId = urlParams.get('variant');
          }
        }

        if (!variantId) {
          variantId = btn.getAttribute('data-current-variant-id');
        }

        const { currency } = detectContext();

        const variantsScript = section.querySelector('.nhd-product-variants-data') || btn.parentElement?.querySelector('.nhd-product-variants-data') || document.querySelector('.nhd-product-variants-data');
        if (variantsScript && variantsScript.textContent) {
          try {
            const variants = JSON.parse(variantsScript.textContent);
            if (Array.isArray(variants)) {
              let matched = null;
              if (variantId) {
                matched = variants.find((v) => String(v.id) === String(variantId));
              }
              if (!matched && variants.length > 0) {
                matched = variants.find((v) => v.available) || variants[0];
              }
              if (matched && matched.title && matched.title !== 'Default Title') {
                variantTitle = matched.title;
                if (matched.price) {
                  variantPrice = formatMoney(matched.price, currency);
                }
              }
            }
          } catch (err) {}
        }

        if (!variantTitle) {
          const variantPicker = section.querySelector('variant-picker') || document.querySelector('variant-picker');
          if (variantPicker) {
            const checkedRadios = Array.from(variantPicker.querySelectorAll('input[type="radio"]:checked'))
              .map((r) => r.value?.trim())
              .filter(Boolean);

            if (checkedRadios.length > 0) {
              variantTitle = checkedRadios.join(' / ');
            } else {
              const selects = Array.from(variantPicker.querySelectorAll('select'))
                .map((s) => (s.options[s.selectedIndex]?.value || s.value || '').trim())
                .filter(Boolean);
              if (selects.length > 0) {
                variantTitle = selects.join(' / ');
              }
            }

            if (!variantTitle) {
              const vpJsonScript = variantPicker.querySelector('script[type="application/json"]');
              if (vpJsonScript && vpJsonScript.textContent) {
                try {
                  const vpData = JSON.parse(vpJsonScript.textContent);
                  if (vpData && vpData.title && vpData.title !== 'Default Title') {
                    variantTitle = vpData.title;
                    if (vpData.price && !variantPrice) {
                      variantPrice = formatMoney(vpData.price, currency);
                    }
                  }
                } catch (e) {}
              }
            }
          }
        }

        if (!variantTitle) {
          const initial = btn.getAttribute('data-initial-variant');
          if (initial && initial !== 'Default Title') {
            variantTitle = initial;
          }
        }

        if (variantTitle === 'Default Title') {
          variantTitle = '';
        }

        return { variantId, variantTitle, variantPrice };
      }

      function handleVariantUpdate(e) {
        if (e && e.detail && e.detail.resource) {
          const res = e.detail.resource;
          btn.setAttribute('data-current-variant-id', res.id);
          if (res.title && res.title !== 'Default Title') {
            btn.setAttribute('data-current-variant-title', res.title);
          }
          if (res.price) {
            const { currency } = detectContext();
            btn.setAttribute('data-current-variant-price', formatMoney(res.price, currency));
          }
        }
      }

      section.addEventListener('variant:update', handleVariantUpdate);
      document.addEventListener('variant:update', handleVariantUpdate);

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const rawPhone = btn.getAttribute('data-phone');
        const phone = cleanPhoneNumber(rawPhone);
        if (!phone) return;

        const { lang } = detectContext();
        const dict = I18N[lang] || I18N.id;

        const form = btn.closest('form') || section.querySelector('form[data-type="add-to-cart-form"]') || document.querySelector('form[data-type="add-to-cart-form"]');
        const productTitle = btn.getAttribute('data-product-title') || document.querySelector('.product-title, h1')?.textContent?.trim() || 'Koleksi Nahid Studio';
        const productUrl = btn.getAttribute('data-product-url') || window.location.href;

        const resolved = resolveCurrentVariant();
        const variantTitle = btn.getAttribute('data-current-variant-title') || resolved.variantTitle;

        const livePriceText = getLivePriceText(section);
        const variantPrice = livePriceText || resolved.variantPrice || btn.getAttribute('data-current-variant-price') || btn.getAttribute('data-product-price') || '';

        const quantityInput = form ? form.querySelector('input[name="quantity"]') : section.querySelector('input[name="quantity"]');
        const qty = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

        let optionLabel = dict.labelVariant;
        const optionsScript = section.querySelector('.nhd-product-options-data') || btn.parentElement?.querySelector('.nhd-product-options-data');
        if (optionsScript && optionsScript.textContent) {
          try {
            const options = JSON.parse(optionsScript.textContent);
            if (Array.isArray(options) && options.length === 1) {
              const optName = (options[0].name || '').toLowerCase();
              if (optName.includes('size') || optName.includes('ukuran')) {
                optionLabel = dict.labelSize;
              } else if (optName.includes('color') || optName.includes('warna')) {
                optionLabel = dict.labelColor;
              }
            }
          } catch (e) {}
        } else if (variantTitle) {
          if (/^(XS|S|M|L|XL|XXL|All Size|S-M|L-XL|Custom)/i.test(variantTitle.trim())) {
            optionLabel = dict.labelSize;
          }
        }

        const customSpecs = [];
        if (form) {
          const sleeve = form.querySelector('input[name="properties[Sleeve length]"]')?.value?.trim();
          const abaya = form.querySelector('input[name="properties[Abaya length]"]')?.value?.trim();
          const notes = form.querySelector('textarea[name="properties[Order notes]"]')?.value?.trim();
          if (sleeve) customSpecs.push(`Sleeve Length: ${sleeve}`);
          if (abaya) customSpecs.push(`Abaya Length: ${abaya}`);
          if (notes) customSpecs.push(`${dict.labelNotes}: ${notes}`);
        }

        let message = `${dict.greetingProduct}\n\n`;
        message += `• *${dict.labelProduct}*: ${productTitle}\n`;
        if (variantTitle) {
          message += `• *${optionLabel}*: ${variantTitle}\n`;
        }
        if (customSpecs.length > 0) {
          message += `• *${dict.labelCustom}*: ${customSpecs.join(' | ')}\n`;
        }
        message += `• *${dict.labelQty}*: ${qty} pcs\n`;
        if (variantPrice) {
          message += `• *${dict.labelPrice}*: ${variantPrice}\n`;
        }
        message += `• *${dict.labelLink}*: ${productUrl}\n`;
        message += formatCustomerSection(btn, form, dict);
        message += `\n${dict.closingProduct}`;

        const waUrl = `https://api.whatsapp.com/send?phone=${encodeURIComponent(phone)}&text=${encodeURIComponent(message)}`;
        window.open(waUrl, '_blank');
      });
    });
  }

  // Cart Handler
  function initCartWhatsApp() {
    const cartButtons = document.querySelectorAll('[data-nhd-whatsapp-cart]');
    cartButtons.forEach((btn) => {
      if (btn.dataset.initialized === 'true') return;
      btn.dataset.initialized = 'true';

      btn.addEventListener('click', async function (e) {
        e.preventDefault();
        const rawPhone = btn.getAttribute('data-phone');
        const phone = cleanPhoneNumber(rawPhone);
        if (!phone) return;

        const { lang, currency: detectedCurrency } = detectContext();
        const dict = I18N[lang] || I18N.id;

        btn.style.opacity = '0.6';
        btn.style.pointerEvents = 'none';

        try {
          const res = await fetch('/cart.js');
          const cart = await res.json();

          if (!cart || cart.item_count === 0) {
            alert(dict.cartEmpty);
            return;
          }

          const cartCurrency = (cart.currency || detectedCurrency || 'IDR').toUpperCase();
          let message = `${dict.greetingCart}\n\n*${dict.orderSummaryHeading}:*\n`;

          cart.items.forEach((item, index) => {
            const itemPrice = formatMoney(item.final_line_price || item.line_price, cartCurrency);
            const variantInfo = item.variant_title && item.variant_title !== 'Default Title' ? ` (${item.variant_title})` : '';
            message += `${index + 1}. *${item.product_title}*${variantInfo}\n   └ ${item.quantity} x ${formatMoney(item.final_price, cartCurrency)} = ${itemPrice}\n`;
          });

          const totalFormatted = formatMoney(cart.total_price, cartCurrency);
          message += `\n• *${dict.labelTotal}*: ${totalFormatted}\n`;

          if (cart.note) {
            message += `• *${dict.labelNotes}*: ${cart.note}\n`;
          }

          message += formatCustomerSection(btn, null, dict);

          message += `\n${dict.closingCart}`;

          const waUrl = `https://api.whatsapp.com/send?phone=${encodeURIComponent(phone)}&text=${encodeURIComponent(message)}`;
          window.open(waUrl, '_blank');
        } catch (err) {
          console.error('Error creating WhatsApp cart message:', err);
          window.open(`https://api.whatsapp.com/send?phone=${encodeURIComponent(phone)}`, '_blank');
        } finally {
          btn.style.opacity = '1';
          btn.style.pointerEvents = 'auto';
        }
      });
    });
  }

  function init() {
    syncDynamicLabels();
    initProductWhatsApp();
    initCartWhatsApp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Standard Lifecycle and Switcher Events (No body mutation loops!)
  window.addEventListener('currency:change', syncDynamicLabels);
  window.addEventListener('shopify:currency:change', syncDynamicLabels);
  window.addEventListener('localization:change', syncDynamicLabels);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('cart:update', init);
  document.addEventListener('cart-drawer:open', init);

  document.addEventListener('change', (e) => {
    if (
      e.target &&
      (e.target.name === 'country_code' ||
        e.target.name === 'currency_code' ||
        e.target.name === 'locale_code' ||
        e.target.closest('.localization-form') ||
        e.target.closest('[data-localization-form]'))
    ) {
      setTimeout(syncDynamicLabels, 100);
    }
  });

  // Only observe root html element for lang/currency attribute changes with a debounced handler
  let rootDebounce = null;
  const rootObserver = new MutationObserver(() => {
    if (rootDebounce) clearTimeout(rootDebounce);
    rootDebounce = setTimeout(syncDynamicLabels, 200);
  });
  rootObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['lang', 'data-currency', 'data-locale']
  });
})();
